import React, { useRef, useState, useEffect } from 'react';
import './AiMlPortfolio.css';
import demoBg from '../dynamic-ai-background-with-machine-learning-graphics-bold-colors_837816-21303.avif';

// Project Images
import churnImg from '../assets/projects/churn.png';
import medicalImg from '../assets/projects/medical.png';
import chatImg from '../assets/projects/chat.png';
import supplyImg from '../assets/projects/supply.png';
import droneImg from '../assets/projects/drone.png';
import agricultureImg from '../assets/projects/agriculture.png';

// Service Images
import predictiveImg from '../assets/services/predictive.jpg';
import nlpImg from '../assets/services/nlp.jpg';
import visionImg from '../assets/services/vision.jpg';
import generativeImg from '../assets/services/generative.jpg';
import mlopsImg from '../assets/services/mlops.jpg';
import rpaImg from '../assets/services/rpa.jpg';
import edgeImg from '../assets/services/edge.jpg';
import voiceAiImg from '../assets/services/voice_ai_icon.png';

const RevealText = ({ children, delay = "0s", className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    const { current } = domRef;
    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, []);

  return (
    <div className={`line-wrapper ${className}`} ref={domRef}>
      <span 
        className={`reveal-text ${isVisible ? 'visible' : ''}`}
        style={{ transitionDelay: delay }}
      >
        {children}
      </span>
    </div>
  );
};

const IndustryCard = ({ icon, title, description, points, image }) => {
  const [isActive, setIsActive] = useState(false);
  
  return (
    <div 
      className={`p-industry-card ${isActive ? 'active' : ''}`}
      onClick={() => setIsActive(!isActive)}
    >
      <div 
        className="p-card-background" 
        style={{ backgroundImage: `url(${image})` }}
      ></div>
      <div className="p-card-overlay"></div>
      <div className="p-card-content">
        <div className="p-industry-icon">{icon}</div>
        <RevealText delay="0.1s"><h3>{title}</h3></RevealText>
        <RevealText delay="0.2s"><p>{description}</p></RevealText>
        {points && (
          <ul className="p-industry-points">
            {points.map((point, idx) => (
              <RevealText key={idx} delay={`${0.3 + idx * 0.1}s`}>
                <li>{point}</li>
              </RevealText>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const ServiceCard = ({ title, description, icon, image, isSelected, onClick }) => (
  <div className={`service-card ${isSelected ? 'selected' : ''}`} onClick={onClick}>
    <div className="service-card-image">
      {image ? (
        <img src={image} alt={title} />
      ) : (
        <div className="service-card-image-placeholder">{icon}</div>
      )}
    </div>
    <div className="service-card-content">
      <div className="service-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{description}</p>
      {!isSelected && (
        <div className="service-card-footer">
          <span className="learn-more">Learn More →</span>
        </div>
      )}
    </div>
  </div>
);

const DemoView = ({ onBack }) => {
  const [testImage, setTestImage] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const fileInputRef = useRef(null);

  const handleZoneClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTestImage(reader.result);
        setPrediction(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTestRun = (e) => {
    e.stopPropagation();
    if (!testImage) return;

    setIsAnalyzing(true);
    setPrediction(null);
    setTimeout(() => {
      setIsAnalyzing(false);
      setPrediction({
        label: "Industrial Component Detected",
        confidence: "99.2%",
        status: "Optimal Condition"
      });
    }, 2500);
  };

  const handleReset = (e) => {
    e.stopPropagation();
    setTestImage(null);
    setPrediction(null);
  };

  return (
    <div className="demo-view-container" style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.8), rgba(0,0,0,0.8)), url(${demoBg})` }}>
      <div className="demo-header">
        <button className="back-link" onClick={onBack}>← Back to Portfolio</button>
        <h1>
          <RevealText>Interactive AI/ML Demo</RevealText>
        </h1>
        <p>
          <RevealText delay="0.2s">Experience our models in action with real-world scenarios.</RevealText>
        </p>
      </div>

      {/* Video Walkthrough Section */}
      <section className="demo-section video-demo">
        <div className="section-header">
          <h2><RevealText>Video Walkthrough</RevealText></h2>
          <p><RevealText delay="0.2s">A short overview of our typical development pipeline and deployment strategy.</RevealText></p>
        </div>
        <div className="video-container main-demo-visual">
          {isVideoPlaying ? (
            <iframe 
              width="100%" 
              height="100%" 
              src="https://www.youtube.com/embed/5NgNicANyqM?autoplay=1" 
              title="Tech Video Walkthrough" 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen
              style={{ border: 'none' }}
            ></iframe>
          ) : (
            <div className="ai-visualizer-overlay" onClick={() => setIsVideoPlaying(true)} style={{ cursor: 'pointer' }}>
              <div className="play-button-icon">▶</div>
              <p>Load Neural Hub Walkthrough</p>
            </div>
          )}
        </div>
      </section>

      {/* Interactive Model Section */}
      <section className="demo-section interactive-model">
        <div className="section-header">
          <h2><RevealText>Computer Vision Test Bench</RevealText></h2>
          <p><RevealText delay="0.2s">Upload your own image to see our anomaly detection model in action.</RevealText></p>
        </div>
        <div className="model-tester">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleImageChange}
            accept="image/*"
          />
          <div className={`upload-zone ${testImage ? 'has-image' : ''}`} onClick={handleZoneClick}>
            {testImage ? (
              <div className="image-preview-container">
                <img src={testImage} alt="Test preview" className="test-image-preview" />
                {prediction ? (
                  <div className="prediction-overlay animated">
                    <div className="prediction-info">
                      <div className="info-row"><span>Detected:</span> {prediction.label}</div>
                      <div className="info-row"><span>Confidence:</span> {prediction.confidence}</div>
                      <div className="info-row"><span>Status:</span> {prediction.status}</div>
                    </div>
                    <button className="action-btn reset" onClick={handleReset}>Upload New</button>
                  </div>
                ) : (
                  <div className="analysis-overlay">
                    {isAnalyzing ? (
                       <div className="analyzing-status">
                        <div className="spinner"></div>
                        <p>Scanning Pixels...</p>
                      </div>
                    ) : (
                      <button className="action-btn" onClick={handleTestRun}>Run AI Analysis</button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="dropzone-content">
                <div className="icon">📸</div>
                <h3>Select Image for Analysis</h3>
                <p>Click to browse or drag and drop your image here</p>
                <div className="upload-hint">Supported: JPG, PNG, WEBP</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Dashboard Preview Section */}
      <section className="demo-section dashboard-preview">
        <div className="section-header">
          <h2><RevealText>Predictive Maintenance Dashboard</RevealText></h2>
          <p><RevealText delay="0.2s">Real-time telemetry and anomaly detection metrics from our manufacturing deployments.</RevealText></p>
        </div>
        <div className="dashboard-grid">
          <div className="dashboard-card">
            <h3>Fleet Status</h3>
            <div className="simple-metric">94% Healthy</div>
          </div>
          <div className="dashboard-card">
            <h3>Anomalies Detected (24h)</h3>
            <div className="simple-metric alert">12 Issues</div>
          </div>
          <div className="dashboard-card chart-card">
            <h3>Vibration Analysis</h3>
            <div className="mini-chart">
              <div className="wave"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Highlight Section */}
      <section className="demo-section narrate-case">
        <div className="case-narrative-card">
          <div className="narrative-badge">Flagship Demo</div>
          <h2><RevealText>Fraud Shield 2.0 Deployment</RevealText></h2>
          <p><RevealText delay="0.2s">"How we achieved a 30% reduction in false positives while maintaining 99% detection rates for a Top 10 FinTech partner."</RevealText></p>
          <div className="results-grid">
            <div className="res-item"><span>Before</span> $12M Losses</div>
            <div className="res-item accent"><span>After</span> $4.2M Losses</div>
          </div>
        </div>
      </section>
    </div>
  );
};

const RadialProjectDisplay = ({ projects }) => {
  const [activeProject, setActiveProject] = useState(projects[0]);

  return (
    <div className="radial-display-container">
      <div className="radial-orbital-system">
        {/* Background Concentric Rings (Arcs) */}
        <div className="arc-background">
          <div className="arc-ring ring-inner"></div>
          <div className="arc-ring ring-middle"></div>
          <div className="arc-ring ring-outer"></div>
        </div>

        {/* Central Hub */}
        <div className="radial-core">
          <div className="core-content">
            <span className="core-label">AI ENGINE</span>
            <div className="core-logo">C-HUB</div>
          </div>
        </div>

        {/* Interaction Slices (Wider hit areas) */}
        <div className="orbital-slices">
          {projects.map((project, index) => {
            const angle = (index * (180 / (projects.length - 1))) - 90;
            const isActive = activeProject?.title === project.title;
            return (
              <div
                key={index}
                className={`project-slice ${isActive ? 'active' : ''}`}
                style={{ '--rotation': `${angle}deg` }}
                onMouseEnter={() => setActiveProject(project)}
                onClick={() => setActiveProject(project)}
              >
                <div className="slice-spoke"></div>
                <div className="slice-node-container">
                  <div className="slice-node">
                    <div className="node-glow"></div>
                  </div>
                  <div className="slice-label-wrapper">
                    <span className="slice-label">{project.title}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Refined Project Info Panel */}
      <div className="project-highlight-panel">
        {activeProject && (
          <div className="highlight-content">
            <div className="highlight-header">
              <div className="project-image-preview">
                <img src={activeProject.image} alt={activeProject.title} />
              </div>
              <h3>{activeProject.title}</h3>
              <div className="header-line"></div>
            </div>
            <p>{activeProject.description}</p>
            <div className="highlight-tech">
              {activeProject.tech.map((t, i) => (
                <span key={i} className="highlight-tag">{t}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const typingWords = ["the Future", "Growth", "Innovation", "Success"];

const AiMlPortfolio = ({ onContactClick }) => {
  const [showDemo, setShowDemo] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [currentHeroProject, setCurrentHeroProject] = useState(0);
  
  // Typing animation state
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Dream Team Form State
  const [dreamFormData, setDreamFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });
  const [isDreamSubmitting, setIsDreamSubmitting] = useState(false);

  const handleDreamChange = (e) => {
    const { name, value } = e.target;
    setDreamFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDreamSubmit = async (e) => {
    e.preventDefault();
    setIsDreamSubmitting(true);
    
    const data = {
      name: dreamFormData.fullName,
      email: dreamFormData.email,
      subject: `[Dream Team] ${dreamFormData.company} - ${dreamFormData.service || 'No Service Selected'}`,
      message: dreamFormData.message,
    };

    try {
      await fetch("https://script.google.com/macros/s/AKfycbwgFAg34DROrnxktJS3ygwNVQadDDGfDK5e0QTwQM5XkzZt_bEZy-UoEvxdtzs6KfHi/exec", {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      setDreamFormData({
        fullName: '',
        email: '',
        company: '',
        service: '',
        message: ''
      });
      alert('Message sent successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsDreamSubmitting(false);
    }
  };
  
  const solutionsRef = useRef(null);

  const projects = [
    {
      title: "Customer Churn Prediction",
      description: "Developed a high-accuracy predictive model to identify customers at risk of leaving, allowing for proactive retention strategies.",
      tech: ["Python", "Scikit-Learn", "Pandas", "XGBoost"],
      image: churnImg,
      link: "#"
    },
    {
      title: "Medical Image Segmentation",
      description: "Deep learning models for automated detection of anomalies in X-rays and MRIs with 98% precision.",
      tech: ["TensorFlow", "Keras", "OpenCV", "UNet"],
      image: medicalImg,
      link: "#"
    },
    {
      title: "Conversational AI Agent",
      description: "Multi-lingual chatbot system capable of handling complex customer service inquiries using NLP.",
      tech: ["PyTorch", "HuggingFace", "FastAPI", "React"],
      image: chatImg,
      link: "#"
    },
    {
      title: "Supply Chain Optimizer",
      description: "Reinforcement learning based system to minimize logistics costs and improve delivery timelines.",
      tech: ["Ray RLib", "AWS", "Python", "Docker"],
      image: supplyImg,
      link: "#"
    },
    {
      title: "Autonomous Drone Navigation",
      description: "Computer vision and sensor fusion algorithms for complex obstacle avoidance and path planning.",
      tech: ["ROS", "YOLOv8", "C++", "Cuda"],
      image: droneImg,
      link: "#"
    },
    {
      title: "Precision Agriculture AI",
      description: "Satellite imagery analysis for crop health monitoring and yield prediction using CNNs.",
      tech: ["PyTorch", "Gradients", "PostGIS", "QGIS"],
      image: agricultureImg,
      link: "#"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHeroProject((prev) => (prev + 1) % projects.length);
    }, 5000); // Change project every 5 seconds
    return () => clearInterval(timer);
  }, [projects.length]);

  // Typing animation effect
  useEffect(() => {
    const currentWord = typingWords[currentWordIndex];
    let typeSpeed = isDeleting ? 50 : 100;

    const timer = setTimeout(() => {
      if (!isDeleting && currentText === currentWord) {
        setTimeout(() => setIsDeleting(true), 2000); // Pause at end of word
      } else if (isDeleting && currentText === '') {
        setIsDeleting(false);
        setCurrentWordIndex((prev) => (prev + 1) % typingWords.length);
      } else {
        const nextText = isDeleting 
          ? currentWord.substring(0, currentText.length - 1)
          : currentWord.substring(0, currentText.length + 1);
        setCurrentText(nextText);
      }
    }, (!isDeleting && currentText === currentWord) ? 2000 : (isDeleting && currentText === '' ? 500 : typeSpeed));

    return () => clearTimeout(timer);
  }, [currentText, isDeleting, currentWordIndex]);

  const scrollToSolutions = () => {
    solutionsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (showDemo) {
    return <DemoView onBack={() => setShowDemo(false)} />;
  }

  return (
    <div className="portfolio-container">
      {/* Header / Hero Section */}
      <section className="staffing-hero-section">
        <div className="hero-container">
          <div className="hero-left">
            <div className="hero-badge">
              <span className="dot"></span> NEXT-GEN AI/ML SOLUTIONS
            </div>
            
            <h1 className="hero-main-title">
              Building Intelligence That <br />
              Drives <span className="gradient-text italic">{currentText}</span>
              <span className="typing-cursor">|</span>
            </h1>
            
            <p className="hero-description-text">
              Transform your enterprise with cutting-edge predictive models and automation frameworks. 
              We precision-engineer bespoke AI solutions to accelerate your digital evolution.
            </p>
            
            <div className="hero-cta-group">
              <button className="hero-btn-primary" onClick={scrollToSolutions}>
                Explore Solutions <span className="arrow">→</span>
              </button>
              <button className="hero-btn-secondary" onClick={() => setShowDemo(true)}>
                Watch Demo
              </button>
            </div>
            
            <div className="hero-stats-pills">
              <div className="hero-pill">✦ 99.9% Accuracy</div>
              <div className="hero-pill">✦ {'<50ms'} Latency</div>
              <div className="hero-pill">✦ 50+ Models Deployed</div>
            </div>
          </div>

          <div className="hero-right">
            <div className="hero-image-wrapper carousel-wrapper">
              <div className="hero-glow-effect"></div>
              
              {projects.map((project, index) => (
                <div 
                  key={index} 
                  className={`carousel-slide ${index === currentHeroProject ? 'active' : ''}`}
                >
                  <img 
                    src={project.image} 
                    alt={project.title} 
                    className="staffing-hero-image" 
                  />
                  
                  <div className="hero-project-info-card">
                    <h3 className="project-card-title">{project.title}</h3>
                    <p className="project-card-desc">{project.description}</p>
                    <div className="project-card-tech">
                      {project.tech.map((t, idx) => (
                        <span key={idx} className="tech-tag">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              <div className="carousel-indicators">
                {projects.map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-dot ${index === currentHeroProject ? 'active' : ''}`}
                    onClick={() => setCurrentHeroProject(index)}
                    aria-label={`Go to project ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Scrolling Tech Marquee */}
        <div className="tech-marquee-container">
          <div className="tech-marquee-track">
            {/* Duplicated list for seamless scrolling */}
            <div className="marquee-content">
              <span>Predictive Analytics</span><span className="dot-sep">•</span>
              <span>Computer Vision</span><span className="dot-sep">•</span>
              <span>Natural Language Processing</span><span className="dot-sep">•</span>
              <span>Generative AI</span><span className="dot-sep">•</span>
              <span>MLOps</span><span className="dot-sep">•</span>
              <span>Recommendation Systems</span><span className="dot-sep">•</span>
              <span>Edge AI</span><span className="dot-sep">•</span>
              <span>Intelligent Automation</span><span className="dot-sep">•</span>
            </div>
            <div className="marquee-content" aria-hidden="true">
              <span>Predictive Analytics</span><span className="dot-sep">•</span>
              <span>Computer Vision</span><span className="dot-sep">•</span>
              <span>Natural Language Processing</span><span className="dot-sep">•</span>
              <span>Generative AI</span><span className="dot-sep">•</span>
              <span>MLOps</span><span className="dot-sep">•</span>
              <span>Recommendation Systems</span><span className="dot-sep">•</span>
              <span>Edge AI</span><span className="dot-sep">•</span>
              <span>Intelligent Automation</span><span className="dot-sep">•</span>
            </div>
          </div>
        </div>
      </section>

      {/* Vertical Services Segment (Now Scrolling) */}
      <section className="vertical-services-segment">
        <div className="scrolling-services-track">
          {[1, 2].map((set) => (
            <div key={set} className="scrolling-services-content" aria-hidden={set === 2}>
              <div className="vertical-service-item">
                <div className="vertical-icon">🤖</div>
                <div className="vertical-text">
                  <h3>Ai Chatbot</h3>
                  <p>Intelligent, conversational agents designed to handle inquiries 24/7.</p>
                </div>
              </div>
              
              <div className="vertical-service-item">
                <div className="vertical-icon">📊</div>
                <div className="vertical-text">
                  <h3>CRM</h3>
                  <p>Automate lead management and deeply understand your customer relationships.</p>
                </div>
              </div>
              
              <div className="vertical-service-item">
                <div className="vertical-icon">💬</div>
                <div className="vertical-text">
                  <h3>WhatsApp Chatbox</h3>
                  <p>Deploy AI directly into WhatsApp to reach customers where they are.</p>
                </div>
              </div>
              
              <div className="vertical-service-item">
                <div className="vertical-icon">🧠</div>
                <div className="vertical-text">
                  <h3>AI/ML Chatbots</h3>
                  <p>Advanced machine learning bots capable of complex reasoning and tasks.</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Overview Section */}
      <section className="overlap-section overview-section">
        <div className="section-header">
          <RevealText className="section-label" delay="0.1s">OVERVIEW</RevealText>
          <RevealText delay="0.2s"><h2>cabptoid’s AI/ML Expertise</h2></RevealText>
        </div>
        <div className="overview-content">
          <div className="overview-text-block">
            <RevealText delay="0.3s">At cabptoid Solutions, we bridge the gap between complex data and actionable insights.</RevealText>
            <RevealText delay="0.4s">Our expertise spans across deep learning, predictive modeling, and natural language processing.</RevealText>
          </div>
          <div className="mission-statement">
            <blockquote>
              <RevealText delay="0.5s">
                "Our mission is to leverage cutting-edge AI and ML technologies to solve industry-specific
                challenges, driving efficiency, innovation, and sustainable growth for our global partners."
              </RevealText>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Solutions / Services Section */}
      <section className="solutions-section" ref={solutionsRef}>
        <div className="section-header">
          <RevealText className="section-label" delay="0.1s">SOLUTIONS</RevealText>
          <RevealText delay="0.2s"><h2>AI/ML Services Overview</h2></RevealText>
        </div>
        <div className="services-grid">
          {[
            {
              id: "predictive",
              icon: "📈",
              title: "Predictive Analytics",
              description: "Anticipate market trends, customer behavior, and operational risks with forecasting models.",
              extendedDesc: "Our predictive analytics solutions harness historical data to construct highly accurate models that forecast future outcomes. By employing advanced machine learning algorithms, we empower organizations to move from reactive strategies to proactive decision-making, significantly lowering risk and optimizing resource allocation.",
              features: ["Demand Forecasting", "Customer Churn Prediction", "Risk Assessment", "Inventory Optimization"],
              image: predictiveImg
            },
            {
              id: "voiceai",
              icon: "🎙️",
              title: "Voice AI",
              description: "Inbound voice agents, Outbound voice agents, Lead reactivation agents.",
              extendedDesc: "Our Voice AI solutions provide 24/7 intelligent call answering, appointment booking, and seamless integrations to handle incoming and outgoing calls with a natural, human-sounding voice. Elevate your customer experience and capture every opportunity without expanding your front desk.",
              features: ["24/7 Call Answering", "Appointment Booking", "Custom Call Flows", "Human Escalation"],
              image: voiceAiImg
            },
            {
              id: "nlp",
              icon: "🗣️",
              title: "Natural Language Processing",
              description: "Unlock insights from text and speech. Multi-lingual sentimental analysis and automation.",
              extendedDesc: "Transform unstructured text and voice data into structured, actionable insights. Our NLP services enable businesses to automate customer interactions, extract meaning from massive document repositories, and monitor brand sentiment in real time, bridging the communication gap between human language and machine understanding.",
              features: ["Sentiment Analysis", "Document Summarization", "Named Entity Recognition", "Conversational AI"],
              image: nlpImg
            },
            {
              id: "vision",
              icon: "👁️",
              title: "Computer Vision",
              description: "Real-time object detection, image segmentation, and facial recognition for enhanced security.",
              extendedDesc: "Empower your applications with the ability to 'see' and interpret visual information. We build robust computer vision systems capable of extracting granular details from images and video streams. These systems are critical for automation in manufacturing, advanced security protocols, and innovative retail experiences.",
              features: ["Object Tracking & Detection", "Optical Character Recognition (OCR)", "Medical Image Analysis", "Quality Control Automation"],
              image: visionImg
            },
            {
              id: "generative",
              icon: "🎨",
              title: "Generative AI",
              description: "Custom Large Language Models (LLMs) and stable diffusion for creative content and automation.",
              extendedDesc: "Leverage the frontier of artificial intelligence to generate novel content, code, and designs. We specialize in fine-tuning massive generative models tailor-made for your enterprise data, enabling unprecedented creative output and massive leaps in knowledge worker productivity.",
              features: ["Custom LLM Deployment", "Automated Content Generation", "Code Co-pilots", "Synthetic Data Generation"],
              image: generativeImg
            },
            {
              id: "mlops",
              icon: "⚙️",
              title: "ML Pipelines & Deployment",
              description: "Seamless MLOps for continuous training, monitoring, and scaling of production AI models.",
              extendedDesc: "Building a model is only half the battle; deploying and maintaining it is where true value is realized. Our MLOps frameworks ensure that your AI solutions are robust, scalable, and continuously learning from new data without manual intervention or extended downtime.",
              features: ["Model Versioning & Registry", "Automated Retraining Pipelines", "Performance Drift Monitoring", "Scalable Inference APIs"],
              image: mlopsImg
            },

            {
              id: "rpa",
              icon: "🤖",
              title: "Intelligent Automation",
              description: "Combine AI with RPA to automate complex business processes and decision workflows.",
              extendedDesc: "Move beyond simple rule-based automation. By integrating artificial intelligence into Robotic Process Automation (RPA), we enable systems to handle exceptions, understand unstructured inputs, and make cognitive data-driven decisions, acting as a force multiplier for your workforce.",
              features: ["Cognitive Document Processing", "Automated Data Entry", "Workflow Orchestration", "Intelligent Exception Handling"],
              image: rpaImg
            },
            {
              id: "edge",
              icon: "⚡",
              title: "Edge AI",
              description: "Deploy efficient, low-latency machine learning models directly on IoT and local hardware.",
              extendedDesc: "Bring intelligence to the edge of your network. We optimize and compress deep learning models so they can run efficiently on resource-constrained devices like sensors, cameras, and mobile phones, ensuring zero-latency decision making and enhanced data privacy by analyzing data at the source.",
              features: ["Model Quantization & Pruning", "IoT Sensor Integration", "Real-time Local Inference", "Privacy-Preserving AI"],
              image: edgeImg
            }
          ].map((service) => (
            <React.Fragment key={service.id}>
              <ServiceCard
                icon={service.icon}
                image={service.image}
                title={service.title}
                description={service.description}
                isSelected={selectedService?.id === service.id}
                onClick={() => setSelectedService(selectedService?.id === service.id ? null : service)}
              />
              {selectedService?.id === service.id && (
                <div className="service-inline-details">
                  <div className="service-inline-image">
                    {selectedService.image ? (
                      <img src={selectedService.image} alt={selectedService.title} />
                    ) : (
                      <div className="service-inline-icon-bg">{selectedService.icon}</div>
                    )}
                  </div>
                  <div className="service-inline-text">
                    <button className="service-inline-close" onClick={(e) => { e.stopPropagation(); setSelectedService(null); }}>×</button>
                    <h2>{selectedService.title}</h2>
                    <div className="service-inline-divider"></div>
                    <p className="service-inline-extended">{selectedService.extendedDesc}</p>
                    {selectedService.features && (
                      <ul className="service-inline-features">
                        {selectedService.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    )}
                    <button className="primary-btn mt-4" onClick={(e) => { e.stopPropagation(); onContactClick(); }}>
                      Discuss Your Project
                    </button>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="projects-section">
        <div className="section-header">
          <RevealText className="section-label" delay="0.1s">WORK</RevealText>
          <RevealText delay="0.2s"><h2>Featured Projects</h2></RevealText>
        </div>
        <RadialProjectDisplay projects={projects} />
      </section>

      {/* Industries Served Section */}
      <section className="industries-section">
        <div className="section-header">
          <RevealText className="section-label" delay="0.1s">DOMAINS</RevealText>
          <RevealText delay="0.2s"><h2>Industries Served</h2></RevealText>
        </div>
        <div className="industries-grid">
          <IndustryCard
            icon="🏥"
            title="Healthcare"
            description="Diagnostics and analytics."
            points={["Diagnostics", "Patient Analytics"]}
            image="https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&q=80&w=800"
          />
          <IndustryCard
            icon="💰"
            title="Finance"
            description="Secure financial AI."
            points={["Fraud Detection", "Risk Scoring"]}
            image="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=800"
          />
          <IndustryCard
            icon="🛒"
            title="Retail"
            description="Smart commerce."
            points={["Recommendation Engines", "Demand Forecasting"]}
            image="https://images.unsplash.com/photo-1556742044-3c52d6e88c62?auto=format&fit=crop&q=80&w=800"
          />
          <IndustryCard
            icon="🏭"
            title="Manufacturing"
            description="Industrial intelligence."
            points={["Predictive Maintenance"]}
            image="https://images.unsplash.com/photo-1531297484001-80022131f5a1?auto=format&fit=crop&q=80&w=800"
          />
          <IndustryCard
            icon="🚚"
            title="Logistics"
            description="Autonomous supply chain."
            points={["Route Optimization", "Fleet Tracking"]}
            image="https://images.unsplash.com/photo-1580674285054-bed31e145f59?auto=format&fit=crop&q=80&w=800"
          />
          <IndustryCard
            icon="⚡"
            title="Energy"
            description="Sustainable smart grids."
            points={["Smart Grids", "Demand Response"]}
            image="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&q=80&w=800"
          />
        </div>
      </section>

      {/* Case Studies / Insights Section */}
      <section className="case-studies-section">
        <div className="section-header">
          <RevealText className="section-label" delay="0.1s">SUCCESS STORIES</RevealText>
          <RevealText delay="0.2s"><h2>Case Studies & Insights</h2></RevealText>
        </div>
        <div className="case-study-content">
          <div className="case-card">
            <div className="case-text">
              <h3>Boosting Retail Revenue by 25%</h3>
              <p>Implemented a personalized recommendation engine for an e-commerce giant, increasing average order value significantly.</p>
              <div className="impact-badge">Measured Impact: +25% Revenue</div>
              <div className="stat-visual">
                <div className="bar-graph">
                  <div className="bar" style={{ height: '40%' }}></div>
                  <div className="bar" style={{ height: '60%' }}></div>
                  <div className="bar" style={{ height: '90%' }}></div>
                </div>
                <span>Growth Visualization</span>
              </div>
            </div>
          </div>
          <div className="case-card">
            <div className="case-text">
              <h3>Reducing Risk in FinTech</h3>
              <p>Our real-time fraud detection system reduced unauthorized transactions by 40% within the first quarter of deployment.</p>
              <div className="impact-badge">Measured Impact: 40% Reduction in Fraud</div>
              <div className="stat-visual">
                <div className="circle-graph">
                  <svg viewBox="0 0 36 36" className="circular-chart">
                    <path className="circle-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                    <path className="circle" strokeDasharray="40, 100" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                  </svg>
                </div>
                <span>Risk Reduction: 40%</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Demos / Dashboard Section */}
      <section className="demos-section">
        <div className="section-header">
          <RevealText className="section-label" delay="0.1s">DEMOS</RevealText>
          <RevealText delay="0.2s"><h2>Interactive Dashboards</h2></RevealText>
        </div>
        <div className="demo-showcase">
          <div className="dashboard-placeholder">
            <div className="grid-overlay"></div>
            <div className="demo-content">
              <div className="chart-mockup">
                <div className="line-graph"></div>
                <div className="data-points">
                  <span></span><span></span><span></span><span></span><span></span>
                </div>
              </div>
              <div className="demo-stats">
                <div className="stat"><span>89%</span> Accuracy</div>
                <div className="stat"><span>{'< 50ms'}</span> Latency</div>
              </div>
            </div>
          </div>
          <p className="demo-desc">Live performance metrics of our deployed NLP models handling 10k+ requests/sec.</p>
        </div>
      </section>

      {/* Team & Expertise Section */}
      <section className="team-section">
        <div className="section-header">
          <RevealText className="section-label" delay="0.1s">EXPERTS</RevealText>
          <RevealText delay="0.2s"><h2>Team & Expertise</h2></RevealText>
        </div>
        <div className="team-grid">
          <div className="team-member">
            <div className="member-avatar">
              <svg viewBox="0 0 24 24" width="40" height="40" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
              </svg>
            </div>
            <h3>Data Scientists</h3>
            <p>Specialists in statistical modeling and predictive analysis.</p>
          </div>
          <div className="team-member">
            <div className="member-avatar">
              <svg viewBox="0 0 24 24" width="40" height="40" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
                <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
                <line x1="6" y1="6" x2="6.01" y2="6"></line>
                <line x1="6" y1="18" x2="6.01" y2="18"></line>
              </svg>
            </div>
            <h3>ML Engineers</h3>
            <p>Experts in deploying and scaling production-grade AI models.</p>
          </div>
          <div className="team-member">
            <div className="member-avatar">
              <svg viewBox="0 0 24 24" width="40" height="40" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1v-1.27c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z"></path>
                <path d="M9 14h.01"></path>
                <path d="M15 14h.01"></path>
              </svg>
            </div>
            <h3>AI Researchers</h3>
            <p>Focusing on the next frontier of deep learning and neural nets.</p>
          </div>
        </div>
      </section>

      {/* Dream Team Contact Section */}
      <section className="dream-team-section">
        <div className="dream-team-content">
          <div className="dream-team-left">
            <div className="contact-pill">CONTACT US</div>
            <h2>Let's Build Your<br/>Dream Team</h2>
            <p className="dream-team-desc">
              Ready to elevate your enterprise? Connect with our staffing
              experts to discuss your unique talent requirements.
            </p>
            
            <div className="contact-info-cards">
              <div className="contact-info-card">
                <div className="contact-icon">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                </div>
                <div className="contact-text">
                  <h4>Point of Contact</h4>
                  <p>Vinushna Lakkakula</p>
                </div>
              </div>
              
              <div className="contact-info-card">
                <div className="contact-icon">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                </div>
                <div className="contact-text">
                  <h4>Call Us</h4>
                  <p>+91 63047 65854</p>
                </div>
              </div>
              
              <div className="contact-info-card">
                <div className="contact-icon">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                </div>
                <div className="contact-text">
                  <h4>Email Us</h4>
                  <p>vinushna.lakkakula@cabptoidsolutions.com</p>
                </div>
              </div>
              
              <div className="contact-info-card">
                <div className="contact-icon">
                  <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                </div>
                <div className="contact-text">
                  <h4>Find Us</h4>
                  <p>2nd Floor, Aikya Vihar, Kavuri Hills,<br/>Hyderabad, Telangana – 500033</p>
                </div>
              </div>

              <div className="contact-info-card">
                <div className="contact-icon">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451c.981 0 1.771-.773 1.771-1.729V1.729C24 .774 23.207 0 22.225 0z"/></svg>
                </div>
                <div className="contact-text">
                  <h4>LinkedIn</h4>
                  <p>
                    <a 
                      href="https://www.linkedin.com/in/vinushnalakkakula/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="contact-link"
                    >
                      vinushnalakkakula
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="dream-team-right">
            <form className="dream-team-form-card" onSubmit={handleDreamSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name</label>
                  <input 
                    type="text" 
                    name="fullName"
                    placeholder="John Doe" 
                    value={dreamFormData.fullName}
                    onChange={handleDreamChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  <input 
                    type="email" 
                    name="email"
                    placeholder="john@company.com" 
                    value={dreamFormData.email}
                    onChange={handleDreamChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-group full-width">
                <label>Company</label>
                <input 
                  type="text" 
                  name="company"
                  placeholder="Acme Inc." 
                  value={dreamFormData.company}
                  onChange={handleDreamChange}
                  required
                />
              </div>
              
              <div className="form-group full-width">
                <label>Service Needed</label>
                <div className="select-wrapper">
                  <select 
                    name="service"
                    value={dreamFormData.service}
                    onChange={handleDreamChange}
                    required
                  >
                    <option value="" disabled>Select a service...</option>
                    <option value="Artificial Intelligence">Artificial Intelligence</option>
                    <option value="Generative AI">Generative AI</option>
                    <option value="Agentic AI">Agentic AI</option>
                    <option value="Voice & Conversational AI">Voice & Conversational AI</option>
                    <option value="Staffing">Staffing</option>
                    <option value="Consulting">Consulting</option>
                  </select>
                </div>
              </div>
              
              <div className="form-group full-width">
                <label>Message</label>
                <textarea 
                  name="message"
                  rows="4" 
                  placeholder="Tell us about your staffing needs..."
                  value={dreamFormData.message}
                  onChange={handleDreamChange}
                  required
                ></textarea>
              </div>
              
              <button 
                type="submit" 
                className="dream-team-submit" 
                disabled={isDreamSubmitting}
              >
                {isDreamSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AiMlPortfolio;
